import React, {
  createContext,
  useContext,
  ReactNode,
  useCallback,
  useMemo
} from 'react';
import useMasterData from '../../hooks/useMasterData';

export interface MasterDataItem {
  domain: string;
  key: string;
  value: string;
  display_text: string;
  helper_text: string;
  properties: string;
  display_order: number;
  created_by: string;
  created_date: string | null;
  updated_by: string;
  updated_date: string | null;
  is_dropdown: boolean;
}

export interface MasterDataContextType {
  masterData: MasterDataItem[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  clearError: () => void;
  getByDomain: (domain: string) => MasterDataItem[];
  getDisplayText: (value: string | null, domain: string | null) => string | undefined;
  getLabel: (key: string, metaData: any) => string;
  getDomain: (key: string | null, metaData: any) => string | null;
  getFilterOptions: (domain: string) => any[];
}

const MasterDataContext = createContext<MasterDataContextType | undefined>(
  undefined,
);

interface MasterDataProviderProps {
  children: ReactNode;
}

export const MasterDataProvider: React.FC<MasterDataProviderProps> = ({
  children,
}) => {

  const masterDataHook = useMasterData() as any;

  const masterData = useMemo(() => {
    return masterDataHook?.masterData?.data || []
  }, [masterDataHook.isLoading]);

  const getByDomain = useCallback(
    (domain: string): MasterDataItem[] => {
      return masterData.filter((item: { domain: string; }) => item.domain === domain);
    },
    [masterData],
  );

  const getByValue = useCallback(
    (value: string, domain: string | null): MasterDataItem | undefined => {
      return masterData.find(
        (item: { key: { toString: () => string; }; domain: string; }) =>
          item.key.toString() == value.toString() &&
          (domain ? item.domain == domain : true),
      );
    },
    [masterData],
  );

  const getDisplayText = useCallback(
    (value: string | null, domain: string | null): string | undefined => {
      if (value === null || value === undefined || value === '') {
        return 'N/A';
      }
      return getByValue(value, domain)?.display_text || value;
    },
    [getByValue],
  );

  const getLabel = (key: string, metaData: any) => {
    const item = metaData.find((m: any) => m.alias_name === key);
    return item ? item.label : key;
  };

  const getDomain = (key: string | null, metaData: any) => {
    if (key == null || key == undefined) return null;
    const item = metaData.find((m: any) => m.alias_name === key);
    return item ? item.domain : null;
  };

  const getFilterOptions = (domain: string) => {
    return masterDataHook?.masterData.data?.filter((item: { is_active: boolean; }) => { return item.is_active }).filter((item: { domain: string; }) => item.domain === domain).sort((a: MasterDataItem, b: MasterDataItem) => a.display_order - b.display_order) || [];
  };

  const contextValue = {
    ...masterDataHook,
    getByDomain,
    getByValue,
    getDisplayText,
    getLabel,
    getDomain,
    getFilterOptions
  };

  return (
    <MasterDataContext.Provider value={contextValue}>
      {children}
    </MasterDataContext.Provider>
  );
};

export const useMasterDataContext = (): MasterDataContextType => {
  const context = useContext(MasterDataContext);
  if (context === undefined) {
    throw new Error(
      'useMasterDataContext must be used within a MasterDataProvider',
    );
  }
  return context;
};

export default MasterDataContext;
